import { CheckpointIProps } from '../../types/types'

/**
 * Print layout configuration for US Letter size
 * Using 96 DPI (browser standard) for pixel calculations:
 * - Sheet: 8.5in x 11in = 816px x 1056px at 96 DPI
 * - Sticker cell: 1in x 1in = 96px x 96px at 96 DPI
 * - QR code: 0.85in x 0.85in (with label below)
 * - Left margin: 0.25in = 24px (centers 8in grid horizontally)
 * - Top margin: 0.5in = 48px (centers 10in grid vertically)
 * - Grid: 8 columns x 10 rows = 80 QR codes per page
 */
export const QR_SIZE_RATIO = 0.85 // QR code is 85% of cell size with label below
export const QR_CODES_PER_PAGE = 8 * 10 // columns * rows

/** Splits checkpoints array into pages for printing */
export const splitIntoPages = (
  checkpoints: CheckpointIProps[]
): CheckpointIProps[][] => {
  const pages: CheckpointIProps[][] = []
  for (let i = 0; i < checkpoints.length; i += QR_CODES_PER_PAGE) {
    pages.push(checkpoints.slice(i, i + QR_CODES_PER_PAGE))
  }
  return pages
}

export const PRINT_CONFIG = {
  // Print dimensions in pixels at 96 DPI (browser standard)
  print: {
    dpi: 96,
    sheetWidth: 816, // 8.5 * 96
    sheetHeight: 1056, // 11 * 96
    marginLeft: 24, // 0.25 * 96 (centers 8in grid horizontally)
    marginTop: 48, // 0.5 * 96 (centers 10in grid vertically)
    cellSize: 96, // 1 inch = 96px
    qrSize: 96, // 1 inch QR codes
  },
  columns: 8,
  rowsPerPage: 10,
  // Preview dimensions (scaled down for modal display)
  preview: {
    scale: 0.4,
    sheetWidth: 326, // 816 * 0.4
    sheetHeight: 422, // 1056 * 0.4
    marginLeft: 10, // 24 * 0.4
    marginTop: 19, // 48 * 0.4
    cellSize: 38, // 96 * 0.4
  },
} as const

/**
 * The print window gets its own stylesheet because the cloned content is
 * injected into a blank document with no access to the app's CSS-in-JS.
 * Sizes are in inches so the output is independent of the screen's DPI.
 */
export const buildPrintDocument = (content: string, title: string): string => `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${title}</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @page {
              size: 8.5in 11in;
              margin: 0;
            }
            @media print {
              html, body {
                width: 8.5in;
                height: 11in;
              }
            }
            body {
              margin: 0;
              padding: 0;
              width: 8.5in;
            }
            .print-page {
              width: 8.5in;
              height: 11in;
              padding-top: 0.5in;
              padding-left: 0.25in;
              page-break-after: always;
              overflow: hidden;
            }
            .print-page:last-child {
              page-break-after: auto;
            }
            .qr-grid {
              display: grid;
              grid-template-columns: repeat(${PRINT_CONFIG.columns}, 1in);
              grid-auto-rows: 1in;
              gap: 0;
            }
            .qr-cell {
              width: 1in;
              height: 1in;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 2px;
              overflow: hidden;
            }
            .qr-cell img,
            .qr-cell canvas {
              width: ${QR_SIZE_RATIO}in !important;
              height: ${QR_SIZE_RATIO}in !important;
              max-width: ${QR_SIZE_RATIO}in !important;
              max-height: ${QR_SIZE_RATIO}in !important;
            }
            .qr-label {
              font-size: 6pt;
              font-weight: 500;
              font-family: Arial, sans-serif;
              color: #000;
              line-height: 1;
            }
          </style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `
